import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import ButtonWithSpinner from "../ButtonWithSpinner";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
  formatInternalTicketProtocol,
  getInternalTicketPriorityLabel,
  getInternalTicketSlaLabel,
  getInternalTicketStatusLabel
} from "../../helpers/internalTicket";

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  metaCard: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    marginBottom: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.08)"
        : "rgba(148, 163, 184, 0.08)"
  },
  eventsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  eventCard: {
    padding: theme.spacing(1.25),
    borderRadius: 12
  },
  eventTitle: {
    fontWeight: 700
  },
  eventMeta: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem"
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  quickCommentChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  commentActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1)
  },
  finalCommentBox: {
    marginTop: theme.spacing(1)
  },
  eventDiffList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.6),
    marginTop: theme.spacing(1)
  },
  eventDiffRow: {
    fontSize: "0.86rem",
    color: theme.palette.text.primary
  },
  eventDiffLabel: {
    fontWeight: 700
  }
}));

const defaultForm = {
  title: "",
  description: "",
  priority: "medium",
  status: "open",
  slaDueAt: "",
  assignedUserId: ""
};

const quickCommentOptions = [
  "Aguardando retorno do cliente",
  "Aguardando fornecedor/terceiro",
  "Em analise tecnica",
  "Acao executada, aguardando validacao",
  "Necessario novo alinhamento interno"
];

const InternalTicketDialog = ({
  open,
  onClose,
  ticketId,
  internalTicket,
  onSaved
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [ticketData, setTicketData] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [commentBody, setCommentBody] = useState("");
  const [finalComment, setFinalComment] = useState("");

  useEffect(() => {
    if (!open) return;

    const nextTicketData = internalTicket || null;
    const nextForm = nextTicketData
      ? {
          title: nextTicketData.title || "",
          description: nextTicketData.description || "",
          priority: nextTicketData.priority || "medium",
          status: nextTicketData.status || "open",
          slaDueAt: nextTicketData.slaDueAt
            ? new Date(nextTicketData.slaDueAt).toISOString().slice(0, 16)
            : "",
          assignedUserId: nextTicketData.assignedUserId || ""
        }
      : defaultForm;

    setTicketData(nextTicketData);
    setForm(nextForm);
    setCommentBody("");
    setFinalComment("");
  }, [open, internalTicket]);

  useEffect(() => {
    if (!open || !ticketData?.id) return;

    let active = true;

    const loadInternalTicketDetails = async () => {
      setDetailsLoading(true);
      try {
        const { data } = await api.get(`/internal-tickets/${ticketData.id}`);
        if (active) {
          setTicketData(data);
        }
      } catch (err) {
        if (active) {
          toastError(err);
        }
      } finally {
        if (active) {
          setDetailsLoading(false);
        }
      }
    };

    loadInternalTicketDetails();

    return () => {
      active = false;
    };
  }, [open, ticketData?.id]);

  useEffect(() => {
    if (!open) return;

    let active = true;

    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const { data } = await api.get("/users");
        if (active) {
          setUsers(Array.isArray(data) ? data : data.users || []);
        }
      } catch (err) {
        if (active) {
          setUsers([]);
        }
      } finally {
        if (active) {
          setUsersLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      active = false;
    };
  }, [open]);

  const editingTicket = ticketData || internalTicket || null;
  const isEditing = Boolean(editingTicket?.id);
  const isMovingToDone =
    form.status === "done" && (!editingTicket || editingTicket.status !== "done");

  const handleChange = field => event => {
    const nextValue = event?.target?.value ?? "";

    setForm(prev => ({
      ...prev,
      [field]: nextValue
    }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error("Informe um titulo para o ticket interno.");
      return;
    }

    if (form.status === "done" && !form.assignedUserId) {
      toast.error("Defina um responsavel antes de concluir o ticket interno.");
      return;
    }

    if (isMovingToDone && !String(finalComment || "").trim()) {
      toast.error("Adicione um comentario final para concluir o ticket interno.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ticketId,
        title: form.title.trim(),
        description: form.description?.trim() || "",
        priority: form.priority,
        status: form.status,
        slaDueAt: form.slaDueAt || null,
        assignedUserId: form.assignedUserId || null,
        ...(form.status === "done"
          ? { finalComment: String(finalComment || "").trim() }
          : {})
      };

      const { data } = isEditing
        ? await api.put(`/internal-tickets/${editingTicket.id}`, payload)
        : await api.post("/internal-tickets", payload);

      setTicketData(data);
      toast.success(
        isEditing ? "Ticket interno atualizado." : "Ticket interno criado."
      );
      if (onSaved) onSaved(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAppendQuickComment = text => {
    const normalizedText = String(text || "").trim();
    if (!normalizedText) return;

    setCommentBody(prev => (prev ? `${prev}\n${normalizedText}` : normalizedText));
  };

  const handleCreateComment = async () => {
    if (!isEditing || !editingTicket?.id) return;

    const normalizedBody = String(commentBody || "").trim();
    if (!normalizedBody) {
      toast.error("Informe um comentario para adicionar no historico.");
      return;
    }

    setCommentLoading(true);
    try {
      const { data } = await api.post(
        `/internal-tickets/${editingTicket.id}/comments`,
        { body: normalizedBody }
      );

      setTicketData(data);
      setCommentBody("");
      if (onSaved) onSaved(data);
      toast.success("Comentario adicionado.");
    } catch (err) {
      toastError(err);
    } finally {
      setCommentLoading(false);
    }
  };

  const events = useMemo(
    () => (editingTicket?.events ? editingTicket.events : []),
    [editingTicket]
  );

  const parseEventPayload = event => {
    if (!event?.payload) {
      return null;
    }

    if (typeof event.payload === "string") {
      try {
        return JSON.parse(event.payload);
      } catch (error) {
        return null;
      }
    }

    return event.payload;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="internal-ticket-dialog-title"
    >
      <DialogTitle id="internal-ticket-dialog-title">
        {isEditing ? "Editar ticket interno" : "Novo ticket interno"}
      </DialogTitle>
      <DialogContent dividers>
        {isEditing ? (
          <Paper variant="outlined" className={classes.metaCard}>
            <Typography variant="body2" color="textSecondary">
              Protocolo
            </Typography>
            <Typography variant="h6">
              {formatInternalTicketProtocol(editingTicket)}
            </Typography>
            <div className={classes.chipsRow}>
              <Chip
                size="small"
                label={getInternalTicketStatusLabel(editingTicket.status)}
              />
              <Chip
                size="small"
                label={getInternalTicketPriorityLabel(editingTicket.priority)}
              />
              <Chip
                size="small"
                label={getInternalTicketSlaLabel(editingTicket.slaStatus)}
              />
            </div>
          </Paper>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Titulo"
              variant="outlined"
              margin="dense"
              fullWidth
              value={form.title}
              onChange={handleChange("title")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Prioridade"
              variant="outlined"
              margin="dense"
              fullWidth
              value={form.priority}
              onChange={handleChange("priority")}
            >
              <MenuItem value="low">Baixa</MenuItem>
              <MenuItem value="medium">Media</MenuItem>
              <MenuItem value="high">Alta</MenuItem>
              <MenuItem value="urgent">Urgente</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descricao"
              variant="outlined"
              margin="dense"
              fullWidth
              multiline
              minRows={4}
              value={form.description}
              onChange={handleChange("description")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Status"
              variant="outlined"
              margin="dense"
              fullWidth
              value={form.status}
              onChange={handleChange("status")}
            >
              <MenuItem value="open">Aberto</MenuItem>
              <MenuItem value="in_progress">Em andamento</MenuItem>
              <MenuItem value="waiting">Aguardando terceiro</MenuItem>
              <MenuItem value="done">Concluido</MenuItem>
              <MenuItem value="cancelled">Cancelado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="SLA"
              type="datetime-local"
              variant="outlined"
              margin="dense"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.slaDueAt}
              onChange={handleChange("slaDueAt")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Responsavel"
              variant="outlined"
              margin="dense"
              fullWidth
              value={form.assignedUserId}
              onChange={handleChange("assignedUserId")}
              disabled={usersLoading}
            >
              <MenuItem value="">Sem responsavel</MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {form.status === "done" ? (
            <Grid item xs={12}>
              <TextField
                label="Comentario final"
                placeholder="Descreva o encerramento para registrar no historico."
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                minRows={2}
                required={isMovingToDone}
                value={finalComment}
                onChange={event => setFinalComment(event.target.value)}
                helperText={
                  isMovingToDone
                    ? "Obrigatorio para concluir o ticket interno."
                    : "Opcional para complementar o encerramento."
                }
                className={classes.finalCommentBox}
              />
            </Grid>
          ) : null}
        </Grid>

        {isEditing ? (
          <Box mt={3}>
            <Typography className={classes.sectionTitle}>Comentarios</Typography>
            <div className={classes.quickCommentChips}>
              {quickCommentOptions.map(option => (
                <Chip
                  key={option}
                  size="small"
                  label={option}
                  onClick={() => handleAppendQuickComment(option)}
                  clickable
                  variant="outlined"
                />
              ))}
            </div>
            <TextField
              label="Novo comentario"
              variant="outlined"
              margin="dense"
              fullWidth
              multiline
              minRows={3}
              value={commentBody}
              onChange={event => setCommentBody(event.target.value)}
            />
            <div className={classes.commentActions}>
              <ButtonWithSpinner
                loading={commentLoading}
                onClick={handleCreateComment}
                variant="outlined"
                color="primary"
              >
                Adicionar comentario
              </ButtonWithSpinner>
            </div>
          </Box>
        ) : null}

        {isEditing ? (
          <Box mt={3}>
            <Typography className={classes.sectionTitle}>Historico</Typography>
            <div className={classes.eventsList}>
              {detailsLoading ? (
                <Typography variant="body2" color="textSecondary">
                  Carregando historico...
                </Typography>
              ) : events.length ? (
                events.map(event => {
                  const eventPayload = parseEventPayload(event);
                  const eventChanges = Array.isArray(eventPayload?.changes)
                    ? eventPayload.changes
                    : [];

                  return (
                    <Paper
                      variant="outlined"
                      className={classes.eventCard}
                      key={event.id}
                    >
                      <Typography className={classes.eventTitle}>
                        {event.title}
                      </Typography>
                      <Typography className={classes.eventMeta}>
                        {`${event.user?.name || "Sistema"} - ${new Date(
                          event.createdAt
                        ).toLocaleString("pt-BR")}`}
                      </Typography>
                      {eventChanges.length ? (
                        <div className={classes.eventDiffList}>
                          {eventChanges.map((change, index) => (
                            <Typography
                              variant="body2"
                              key={`${event.id}-change-${change.field || index}`}
                              className={classes.eventDiffRow}
                            >
                              <span className={classes.eventDiffLabel}>
                                {change.label || change.field || "Campo"}:
                              </span>{" "}
                              {change.from || "vazio"} {"->"} {change.to || "vazio"}
                            </Typography>
                          ))}
                        </div>
                      ) : event.description ? (
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line", marginTop: 8 }}
                        >
                          {event.description}
                        </Typography>
                      ) : null}
                    </Paper>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Ainda nao ha eventos registrados.
                </Typography>
              )}
            </div>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <ButtonWithSpinner
          loading={loading}
          onClick={handleSubmit}
          variant="contained"
          color="primary"
        >
          {isEditing ? "Salvar alteracoes" : "Criar ticket interno"}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default InternalTicketDialog;
